import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import Chart from 'react-apexcharts'
import { useIntl } from 'react-intl'
import {
  ArrowUp,
  ArrowDown,
  Map,
  Search,
  MapPin,
  PhoneCall,
  Layout,
  Star,
  HelpCircle,
  Minus,
} from 'react-feather'
import { CardBody, CardTitle } from 'reactstrap'

import { RowContainer } from '../styles'
import { LoadableCard } from '~/components/commons'

import { queries } from '~/constants/mediaQuery'

export const InsightsOverview = ({
  data,
  reviews,
  showOldReports,
  loading,
}) => {
  const intl = useIntl()
  const numberFormatter = new Intl.NumberFormat()

  const getRatingAverage = ratingsAvg => {
    const ratingSum = Object.values(ratingsAvg)?.reduce(
      (acc, cur) => acc + cur,
      0
    )
    const ratingLength = Object.values(ratingsAvg)?.length

    const average = ratingLength === 0 ? 0 : ratingSum / ratingLength
    const averageFixed = average.toFixed(1)

    return averageFixed
  }

  const getCategoryPercent = useCallback((number, total) => {
    if (number === 0) return ''
    const percent = (+number / (total ?? number)) * 100
    return percent.toFixed(1) + '%'
  }, [])

  const getCategoryMax = useCallback(categoryArr => {
    const max = categoryArr.reduce((acc, cur) => (cur.y > acc ? cur.y : acc), 0)
    return max
  }, [])

  const options = useMemo(() => {
    const options = {
      categoryArr: data?.reviewsCategory?.data[0]?.data ?? [],
      total: data?.questionsAndReviews?.data[0]?.total,
      chart: {
        type: 'bar',
        toolbar: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      labels: [
        intl.formatMessage({ id: '1 star' }),
        intl.formatMessage({ id: '2 stars' }),
        intl.formatMessage({ id: '3 stars' }),
        intl.formatMessage({ id: '4 stars' }),
        intl.formatMessage({ id: '5 stars' }),
      ],
      colors: ['#7E72F2'],
      grid: {
        show: false,
      },
      fill: {
        colors: ['#7E72F2'],
        type: 'solid',
        opacity: 1,
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          borderRadiusApplication: 'around',
          borderRadiusWhenStacked: 'all',
          columnWidth: '50%',
        },
      },
      yaxis: {
        decimalsInFloat: 0,
        show: false,
        max: () => getCategoryMax(options.categoryArr),
      },
      xaxis: {
        type: 'category',
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: value => getCategoryPercent(value, options.total),
        dropShadow: {
          enabled: true,
        },
      },
    }

    return options
  }, [data, getCategoryPercent, getCategoryMax, intl])

  const mapsTotal = showOldReports
    ? data?.views?.data[1]?.total ?? 0
    : data?.mapsViews?.total ?? 0
  const mapsComparisonTotal = showOldReports
    ? 0
    : data?.mapsViews?.comparisonTotal
  const mapsComparisonPercentage =
    +mapsComparisonTotal !== 0
      ? (
          ((mapsTotal - mapsComparisonTotal) / mapsComparisonTotal) *
          100
        ).toFixed(2)
      : '0.00'

  const searchTotal = showOldReports
    ? data?.views?.data[0]?.total ?? 0
    : data?.searchViews?.total ?? 0
  const searchComparisonTotal = showOldReports
    ? 0
    : data?.searchViews?.comparisonTotal
  const searchComparisonPercentage =
    +searchComparisonTotal !== 0
      ? (
          ((searchTotal - searchComparisonTotal) / searchComparisonTotal) *
          100
        ).toFixed(2)
      : '0.00'

  const routesTotal = showOldReports
    ? data?.routes?.total ?? 0
    : data?.customerActions?.data[0]?.total ?? 0
  const routesComparisonTotal = showOldReports
    ? 0
    : data?.customerActions?.data[0]?.comparisonTotal
  const routesComparisonPercentage =
    +routesComparisonTotal !== 0
      ? (
          ((routesTotal - routesComparisonTotal) / routesComparisonTotal) *
          100
        ).toFixed(2)
      : '0.00'

  const callsTotal = showOldReports
    ? data?.calls?.total ?? 0
    : data?.customerActions?.data[1]?.total ?? 0
  const callsComparisonTotal = showOldReports
    ? 0
    : data?.customerActions?.data[1]?.comparisonTotal
  const callsComparisonPercentage =
    +callsComparisonTotal !== 0
      ? (
          ((callsTotal - callsComparisonTotal) / callsComparisonTotal) *
          100
        ).toFixed(2)
      : '0.00'

  const websiteTotal = showOldReports
    ? data?.visits?.total ?? 0
    : data?.customerActions?.data[2]?.total ?? 0
  const websiteComparisonTotal = showOldReports
    ? 0
    : data?.customerActions?.data[2]?.comparisonTotal
  const websiteComparisonPercentage =
    +websiteComparisonTotal !== 0
      ? (
          ((websiteTotal - websiteComparisonTotal) / websiteComparisonTotal) *
          100
        ).toFixed(2)
      : '0.00'

  const reviewsTotal = data?.questionsAndReviews?.data[0]?.total ?? 0
  const reviewsComparisonTotal = showOldReports
    ? 0
    : data?.questionsAndReviews?.data[0]?.comparisonTotal
  const reviewsComparisonPercentage =
    +reviewsComparisonTotal !== 0
      ? (
          ((reviewsTotal - reviewsComparisonTotal) / reviewsComparisonTotal) *
          100
        ).toFixed(2)
      : '0.00'

  const questionsTotal = data?.questionsAndReviews?.data[1]?.total ?? 0
  const questionsComparisonTotal = showOldReports
    ? 0
    : data?.questionsAndReviews?.data[1]?.comparisonTotal
  const questionsComparisonPercentage =
    +questionsComparisonTotal !== 0
      ? (
          ((questionsTotal - questionsComparisonTotal) /
            questionsComparisonTotal) *
          100
        ).toFixed(2)
      : '0.00'

  return (
    <RowContainer>
      <LoadableCard loading={loading}>
        <CardBody>
          <CardTitle>{intl.formatMessage({ id: 'Resume' })}</CardTitle>
          <InsightsBody>
            <div className="reviews-graph">
              <a href="/reviews">
                <div>
                  <span>{intl.formatMessage({ id: 'Reviews' })}</span>
                  <div>
                    <span>{getRatingAverage(reviews?.ratingAvg ?? {})}</span>
                    <Star size="23" color="#FFA84C" fill="#FFA84C" />
                  </div>
                </div>
              </a>
              <div style={{ height: '100%' }}>
                <Chart
                  options={options}
                  series={data?.reviewsCategory?.data ?? []}
                  type="bar"
                  width="100%"
                  height="100%"
                />
              </div>
            </div>
            <div className="insights-overview">
              <div>
                <div className="insight-card">
                  <div>
                    <div className="insight-logo">
                      <Map size="30" />
                    </div>
                  </div>
                  <div>
                    <div className="insight-number">
                      {numberFormatter.format(mapsTotal)}
                    </div>
                    <div className="insight-title">
                      {intl.formatMessage({ id: 'Google Maps' })}
                    </div>
                    <div className="insight-percent">
                      {Boolean(mapsComparisonTotal) &&
                        mapsComparisonPercentage !== '0.00' && (
                          <>
                            {mapsComparisonPercentage > 0 ? (
                              <ArrowUp size="12" color="#6BB156" />
                            ) : (
                              <ArrowDown size="12" color="#ED5F5F" />
                            )}
                            {mapsComparisonPercentage}%
                          </>
                        )}
                      {mapsComparisonPercentage === '0.00' && (
                        <>
                          <Minus size="12" color="#F6AD55" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="insight-card">
                  <div>
                    <div className="insight-logo">
                      <Search size="30" />
                    </div>
                  </div>
                  <div>
                    <div className="insight-number">
                      {numberFormatter.format(searchTotal)}
                    </div>
                    <div className="insight-title">
                      {intl.formatMessage({ id: 'Google Search' })}
                    </div>
                    <div className="insight-percent">
                      {Boolean(searchComparisonTotal) &&
                        searchComparisonPercentage !== '0.00' && (
                          <>
                            {searchComparisonPercentage > 0 ? (
                              <ArrowUp size="12" color="#6BB156" />
                            ) : (
                              <ArrowDown size="12" color="#ED5F5F" />
                            )}
                            {searchComparisonPercentage}%
                          </>
                        )}
                      {searchComparisonPercentage === '0.00' && (
                        <>
                          <Minus size="12" color="#F6AD55" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="insight-card">
                  <div>
                    <div className="insight-logo">
                      <MapPin size="30" />
                    </div>
                  </div>
                  <div>
                    <div className="insight-number">
                      {numberFormatter.format(routesTotal)}
                    </div>
                    <div className="insight-title">
                      {intl.formatMessage({ id: 'Routes' })}
                    </div>
                    <div className="insight-percent">
                      {Boolean(routesComparisonTotal) &&
                        routesComparisonPercentage !== '0.00' && (
                          <>
                            {routesComparisonPercentage > 0 ? (
                              <ArrowUp size="12" color="#6BB156" />
                            ) : (
                              <ArrowDown size="12" color="#ED5F5F" />
                            )}
                            {routesComparisonPercentage}%
                          </>
                        )}
                      {routesComparisonPercentage === '0.00' && (
                        <>
                          <Minus size="12" color="#F6AD55" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="insight-card">
                  <div>
                    <div className="insight-logo">
                      <PhoneCall size="30" />
                    </div>
                  </div>
                  <div>
                    <div className="insight-number">
                      {numberFormatter.format(callsTotal)}
                    </div>
                    <div className="insight-title">
                      {intl.formatMessage({ id: 'Calls' })}
                    </div>
                    <div className="insight-percent">
                      {Boolean(callsComparisonTotal) &&
                        callsComparisonPercentage !== '0.00' && (
                          <>
                            {callsComparisonPercentage > 0 ? (
                              <ArrowUp size="12" color="#6BB156" />
                            ) : (
                              <ArrowDown size="12" color="#ED5F5F" />
                            )}
                            {callsComparisonPercentage}%
                          </>
                        )}
                      {callsComparisonPercentage === '0.00' && (
                        <>
                          <Minus size="12" color="#F6AD55" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="insight-card">
                  <div>
                    <div className="insight-logo">
                      <Layout size="30" />
                    </div>
                  </div>
                  <div>
                    <div className="insight-number">
                      {numberFormatter.format(websiteTotal)}
                    </div>
                    <div className="insight-title">
                      {intl.formatMessage({ id: 'Website' })}
                    </div>
                    <div className="insight-percent">
                      {Boolean(websiteComparisonTotal) &&
                        websiteComparisonPercentage !== '0.00' && (
                          <>
                            {websiteComparisonPercentage > 0 ? (
                              <ArrowUp size="12" color="#6BB156" />
                            ) : (
                              <ArrowDown size="12" color="#ED5F5F" />
                            )}
                            {websiteComparisonPercentage}%
                          </>
                        )}
                      {websiteComparisonPercentage === '0.00' && (
                        <>
                          <Minus size="12" color="#F6AD55" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="insight-card">
                  <div>
                    <div className="insight-logo">
                      <Star size="30" />
                    </div>
                  </div>
                  <div>
                    <div className="insight-number">
                      {numberFormatter.format(reviewsTotal)}
                    </div>
                    <div className="insight-title">
                      {intl.formatMessage({ id: 'Reviews' })}
                    </div>
                    <div className="insight-percent">
                      {Boolean(reviewsComparisonTotal) &&
                        reviewsComparisonPercentage !== '0.00' && (
                          <>
                            {reviewsComparisonPercentage > 0 ? (
                              <ArrowUp size="12" color="#6BB156" />
                            ) : (
                              <ArrowDown size="12" color="#ED5F5F" />
                            )}
                            {reviewsComparisonPercentage}%
                          </>
                        )}
                      {reviewsComparisonPercentage === '0.00' && (
                        <>
                          <Minus size="12" color="#F6AD55" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="insight-card">
                  <div>
                    <div className="insight-logo">
                      <HelpCircle size="30" />
                    </div>
                  </div>
                  <div>
                    <div className="insight-number">
                      {numberFormatter.format(questionsTotal)}
                    </div>
                    <div className="insight-title">
                      {intl.formatMessage({ id: 'Questions' })}
                    </div>
                    <div className="insight-percent">
                      {Boolean(questionsComparisonTotal) &&
                        questionsComparisonPercentage !== '0.00' && (
                          <>
                            {questionsComparisonPercentage > 0 ? (
                              <ArrowUp size="12" color="#6BB156" />
                            ) : (
                              <ArrowDown size="12" color="#ED5F5F" />
                            )}
                            {questionsComparisonPercentage}%
                          </>
                        )}
                      {questionsComparisonPercentage === '0.00' && (
                        <>
                          <Minus size="12" color="#F6AD55" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </InsightsBody>
        </CardBody>
      </LoadableCard>
    </RowContainer>
  )
}

const InsightsBody = styled.div`
  display: flex;
  gap: 1em;

  > div {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1em;
    height: 20em;
  }

  @media ${queries.less.mdl} {
    flex-direction: column;
  }

  .reviews-graph {
    flex-basis: 33%;
    flex-grow: 1;

    > a > div:nth-child(1) {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      > span {
        color: #000;
        font-size: 1.3em;
        font-weight: 700;
      }

      > div {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5em;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 0.5em;

        > span {
          color: #000;
          font-size: 1.5em;
          font-weight: 600;
          line-height: 1em;
        }
      }
    }

    > div:nth-child(2) {
      max-height: 15em;
    }
  }

  .insights-overview {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    flex-basis: 66%;
    flex-grow: 2;
    gap: 1em;

    > div {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: space-around;
      gap: 1em;

      @media ${queries.less.mdxs} {
        flex-direction: column;
      }
    }

    .insight-card {
      display: flex;
      align-items: center;
      flex-grow: 1;
      flex-shrink: 1;
      gap: 1em;
      padding: 1em;
      border: 1px solid #e2e8f0;
      border-radius: 5px;

      .insight-logo {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #efefef;
        border-radius: 50%;
        height: 5em;
        width: 5em;
      }

      > div:nth-child(2) {
        .insight-number {
          color: #1a202c;
          font-size: 1.7em;
          font-weight: 600;
        }

        .insight-title {
          color: #2c2c2c;
          font-size: 1em;
        }

        .insight-percent {
          display: flex;
          align-items: center;
          gap: 0.2em;
          color: #8d8d8d;
          font-size: 0.9em;
        }
      }
    }
  }
`
